import { useContext, useEffect, useState } from 'react'
import { Select, TextField } from '@nv/basic-components'
import { ModalContext } from '@nv/contexts'
import { AdminVoterAuthService } from '@nv/services'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { PhoneInput } from '../../../phone_input/PhoneInput'
import 'react-phone-number-input/style.css'
import useNVModal from '../../nv_modal/useNVModal'
import { NVBasicModal } from '../nv_basic_modal/NVBasicModal'
import styles from './NewAdminUser.module.scss'

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  username: '',
  phone: '',
  role: null,
}

export const NewAdminUser = ({ rolesOptions, setRefreshList }: any) => {
  const { t } = useTranslation()
  const { setHeaderLabel, setBtns, onClose } = useContext(ModalContext)
  const { NVModal, openModal } = useNVModal()
  const [status, setStatus] = useState('idle')

  const schema = Yup.object().shape({
    firstName: Yup.string().required(t('admin.users.firstNameRequired')),
    lastName: Yup.string().required(t('admin.users.lastNameRequired')),
    email: Yup.string().email(t('admin.users.wrongEmailFormat')).required(t('form.validations.email')),
    username: Yup.string().required(t('auth.login.validations.username.admin')),
    phone: Yup.string().required(t('form.validations.phone')),
    role: Yup.object().nullable().required(t('admin.users.roleRequired')),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      const userInput = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        username: values.username,
        phoneNumber: values.phone,
        roles: [values.role],
      }

      const result = await AdminVoterAuthService.adminCreateUser(userInput)
      if (result.success) {
        formik.resetForm()
        setRefreshList(true)
        setStatus('success')
      } else {
        //console.error('Error:', result.error)
        openModal()
      }
    },
  })

  useEffect(() => {
    setHeaderLabel(t('admin.users.newAdminUser'))
    setBtns([
      ...(status !== 'idle'
        ? [{ text: t('buttons.ok'), onClick: onClose, variant: 'secondary' }]
        : [
            { text: t('buttons.cancel'), onClick: onClose, variant: 'secondary' },
            { text: t('buttons.createUserAndSend'), onClick: async () => formik.handleSubmit() },
          ]),
    ])
  }, [status])

  if (status !== 'idle') {
    return <NVBasicModal title={t('admin.users.newAdminUser')} text={t('admin.adminUsers.successCreatingNewAdmin')} success={true} />
  }

  return (
    <>
      <NVModal>
        <NVBasicModal title={t('admin.users.newAdminUser')} text={t('admin.adminUsers.errorCreatingNewAdmin')} error={true} />
      </NVModal>

      <div className={styles['inputs__container']}>
        <TextField
          formik={formik}
          type='text'
          field='firstName'
          placeholder={t('form.labels.firstName')}
          label={t('form.labels.firstName')}
          isRequired={true}
          autocomplete='off'
        />

        <TextField
          formik={formik}
          type='text'
          field='lastName'
          placeholder={t('form.labels.lastName')}
          label={t('form.labels.lastName')}
          isRequired={true}
          autocomplete='off'
        />

        <TextField
          formik={formik}
          type='text'
          field='username'
          placeholder={t('form.labels.username')}
          label={t('form.labels.username')}
          isRequired={true}
          autocomplete='off'
        />

        <TextField
          formik={formik}
          type='email'
          field='email'
          placeholder={t('form.labels.enterEmail')}
          label={t('form.labels.email')}
          isRequired={true}
          autocomplete='off'
        />

        <PhoneInput formik={formik} field='phone' isRequired autocomplete='off' />

        <Select
          label={t('admin.dropdown.role')}
          formik={formik}
          options={rolesOptions}
          field='role'
          value={formik.values.role}
          isRequired={true}
          onChange={(item) => formik.setFieldValue('role', item.value)}
        />
      </div>
    </>
  )
}
